import { create } from "zustand";
import { getError } from "../../utils/getError";
import api, { isAbortError } from "../../utils/api";

export interface Status {
  id: string;
  name: string;
  active?: boolean;
  alert_message?: string;
  notification_id?: string;
  company_id?: string | null;
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
}

export interface CreateStatusData {
  active?: boolean;
  alert_message?: string;
  notification_id?: string;
  company_id?: string | null;
  name: string;
}

export interface UpdateGroupData extends Omit<Status, "createdAt" | "updatedAt"> {}

interface StatusesStore {
  statuses: Status[];
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchStatuses: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
    status?: string;
    search?: string;
  }) => Promise<void>;
  createStatus: (data: CreateStatusData) => Promise<{ success: boolean; data?: Status; error?: string }>;
  deleteStatus: (id: string) => Promise<{ success: boolean; error?: string }>;
}

export const useStatusesStore = create<StatusesStore>((set, get) => ({
  statuses: [],
  loading: false,
  error: null,
  meta: undefined,

  fetchStatuses: async (params = {}) => {
    if (get().loading) return;

    set({ loading: true, error: null });
    try {
      const { data } = await api.get("/statusesList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...params,
        },
      });
      set({ statuses: data.data || [], meta: data.meta, loading: false });
    } catch (error: any) {
      if (isAbortError(error)) return;
      const errorMessage = getError(error);
      set({ error: errorMessage, loading: false });
    }
  },

  createStatus: async (data: CreateStatusData) => {
    try {
      const response = await api.post("/statuses", data);
      const newStatus = response.data?.data;
      if (newStatus) {
        set((state) => ({ statuses: [...state.statuses, newStatus] }));
        return { success: true, data: newStatus };
      }
      return { success: false, error: "No se pudo crear el estado" };
    } catch (error: any) {
      const errorMessage = getError(error);
      return { success: false, error: errorMessage };
    }
  },

  deleteStatus: async (id: string) => {
    try {
      await api.delete("/statuses", { params: { id } });
      set((state) => ({ statuses: state.statuses.filter((s) => s.id !== id) }));
      return { success: true };
    } catch (error: any) {
      const errorMessage = getError(error);
      return { success: false, error: errorMessage };
    }
  },
}));
